import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  changelogApi,
  healthApi,
  type ChangelogEntry,
  type VersionInfo,
} from '../lib/api';

const ACTION_COLOR: Record<ChangelogEntry['action'], 'success' | 'warning' | 'error'> = {
  create: 'success',
  update: 'warning',
  delete: 'error',
};

export default function Changelog() {
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [since, setSince] = useState('');

  useEffect(() => {
    healthApi.version().then(setVersion).catch(() => {});
  }, []);

  const load = (targetPage = page) => {
    setLoading(true);
    changelogApi
      .list({
        page: targetPage,
        limit: 50,
        resource: resourceFilter || undefined,
        since: since ? new Date(since).toISOString() : undefined,
      })
      .then((res) => {
        setEntries(res.data);
        setPageCount(res.pagination.pages);
        setTotal(res.pagination.total);
      })
      .catch(() => setError('Failed to load changelog'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceFilter, since]);

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Changelog & Version
      </Typography>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">API Version</Typography>
              <Typography variant="h5" fontWeight={700}>
                {version?.apiVersion ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Data version</Typography>
              <Typography variant="body1" fontWeight={600}>
                {version?.dataVersion ? new Date(version.dataVersion).toLocaleString() : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total changes</Typography>
              <Typography variant="h5" fontWeight={700}>
                {total.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          label="Resource"
          placeholder="e.g. stateForeignQualification"
          value={resourceFilter}
          onChange={(e) => setResourceFilter(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size="small"
          label="Since"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={since}
          onChange={(e) => setSince(e.target.value)}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Resource ID</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No entries</TableCell>
                </TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow key={e._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(e.at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={e.action} color={ACTION_COLOR[e.action]} />
                    </TableCell>
                    <TableCell>{e.resource}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: 12 }}>{e.resourceId ?? '—'}</code>
                    </TableCell>
                    <TableCell>{e.actor?.email ?? e.actor?.sub ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {e.summary ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}
    </Box>
  );
}
